import { z } from "zod";

/**
 * Edit-time schemas for invoice line items. Used by tRPC inputs and RHF forms.
 * Every amount is integer cents; quantities are positive integers.
 *
 * Note: we DO NOT enforce `amount === quantity × unitAmount`. Ad-hoc lines
 * (e.g. a discount, a one-off credit, a rounded settlement) legitimately
 * decouple the two. Validation lives at the field level only.
 */
export const updateLineItemSchema = z
	.object({
		lineItemId: z.string().min(1),
		description: z.string().min(1).max(255).optional(),
		quantity: z.number().int().positive().optional(),
		unitAmount: z.number().int().nonnegative().optional(),
		amount: z.number().int().nonnegative().optional(),
	})
	.refine(
		(v) =>
			v.description !== undefined ||
			v.quantity !== undefined ||
			v.unitAmount !== undefined ||
			v.amount !== undefined,
		{ message: "At least one field must be provided to update" },
	);

export type UpdateLineItemInput = z.infer<typeof updateLineItemSchema>;

export const addLineItemSchema = z.object({
	invoiceId: z.string().min(1),
	description: z.string().min(1).max(255),
	quantity: z.number().int().positive().default(1),
	unitAmount: z.number().int().nonnegative(),
	amount: z.number().int().nonnegative(),
});

export type AddLineItemInput = z.infer<typeof addLineItemSchema>;

export const removeLineItemSchema = z.object({
	lineItemId: z.string().min(1),
});

export type RemoveLineItemInput = z.infer<typeof removeLineItemSchema>;
